import {Subscription} from "rxjs";
import {DatePipe} from "@angular/common";
import {RouterLink} from "@angular/router";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';

import {ToastrService} from "ngx-toastr";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {AdOfferApiService} from "@numizmat/services/core";
import {AdOfferStatus, AdStatus} from '@numizmat/enumerations';
import {AlertService, ModalService} from "@numizmat/services/common";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {PublicAdDetailResponse, PublicAdDetailsReceivedOfferResponse} from "@numizmat/services-models/core";
import {
  AdCounterOfferModalComponent
} from "@numizmat/pages/ad-detail-page/modals/ad-counter-offer-modal/ad-counter-offer-modal.component";

@Component({
  selector: 'nz-ad-offer-section',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink
  ],
  templateUrl: './ad-offer-section.component.html',
  styleUrl: './ad-offer-section.component.scss'
})
export class AdOfferSectionComponent implements OnInit, OnDestroy {
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private toastrService: ToastrService = inject(ToastrService);
  private adOfferApiService: AdOfferApiService = inject(AdOfferApiService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);

  private subs: Subscription = new Subscription();

  protected adDetails!: PublicAdDetailResponse;

  protected readonly AdStatus = AdStatus;
  protected readonly AdOfferStatus = AdOfferStatus;
  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.getAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onOpenAdCounterOfferModal(offer: PublicAdDetailsReceivedOfferResponse): void {
    const modalRef: NgbModalRef = this.modalService.open(AdCounterOfferModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.adId = this.adDetails.adId;
    modalRef.componentInstance.adTitle = this.adDetails.title;
    modalRef.componentInstance.adPrice = this.adDetails.price;
    modalRef.componentInstance.offerId = offer.offerId;
    modalRef.componentInstance.offeredPrice = offer.offeredPrice;
  }

  protected showAdOfferAcceptWarning(offer: PublicAdDetailsReceivedOfferResponse): void {
    this.alertService.showAdOfferAcceptWarning(this.adDetails.title, offer.offeredPrice).then((isConfirmed) => {
      if (isConfirmed) {
        this.adOfferApiService.acceptAdOffer(offer.offerId).subscribe({
          next: response => {
            this.toastrService.success("Успешно потвърдихте предложението!");
          }
        });
      }
    });
  }

  protected showAdOfferRejectWarning(offer: PublicAdDetailsReceivedOfferResponse): void {
    this.alertService.showAdOfferRejectWarning(this.adDetails.title, offer.offeredPrice).then((isConfirmed) => {
      if (isConfirmed) {
        this.adOfferApiService.rejectAdOffer(offer.offerId).subscribe({
          next: response => {
            if (!!response) {
              this.toastrService.success('Успешно остакзахте предложението');
            } else {
              this.toastrService.error('Възникна грешка!');
            }
          }
        });
      }
    });
  }

  protected showAdCounterOfferAcceptWarning(): void {
    const counterOfferPrice: number = this.adDetails.buyerOffer.counterOfferPrice ?? 0;
    this.alertService.showAdCounterOfferAcceptWarning(this.adDetails.title, counterOfferPrice).then((isConfirmed) => {
      if (isConfirmed) {
        this.adOfferApiService.acceptCounterAdOffer(this.adDetails.buyerOffer.offerId).subscribe({
          next: response => {
            this.toastrService.success("Успешно потвърдихте контрапредложението!");
          }
        });
      }
    });
  }

  protected showAdCounterOfferRejectWarning(): void {
    const counterOfferPrice: number = this.adDetails.buyerOffer.counterOfferPrice ?? 0;
    this.alertService.showAdCounterOfferRejectWarning(this.adDetails.title, counterOfferPrice).then((isConfirmed) => {
      if (isConfirmed) {
        this.adOfferApiService.rejectCounterAdOffer(this.adDetails.buyerOffer.offerId).subscribe({
          next: response => {
            this.toastrService.success("Успешно остакзахте контрапредложението!");
          }
        });
      }
    });
  }

  private getAdDetails(): void {
    const subscription: Subscription = this.adDetailStateService.getPublicAdDetails().subscribe({
      next: (adDetails: PublicAdDetailResponse | undefined) => {
        if (!!adDetails) {
          this.adDetails = adDetails;
        }
      }
    });
    this.subs.add(subscription);
  }
}
